import ApplicationController from './application_controller';
import { NEEDS_VALUE_CLASS, MARK_TITLES, markRequiredEmpties, refreshNeedsValue, clearAllMarks, anyMarks, setMarkTitle, clearMarkTitle } from 'helpers/required-field-marks';

// AI Scribe surface — UI shell.
//
// Mounted on the document-fill <form>. Drives the launch button, the transcript panel (a
// right-hand sidebar column), the mask over the form fields, and the status state machine.
//
// Dictation is the EXISTING Soniox voice-dictation controller, mounted around the transcript
// textarea (see _ai_scribe_panel.html.erb). The big mic is its start button; the therapist
// must click it to begin — we never auto-listen. voice-dictation auto-targets the textarea
// and fires an `input` event on each insertion, which drives the auto-fill here.
//
// Routing/fill runs through our self-hosted Gai service, and ONLY when the therapist clicks
// Fill document — dictation just accumulates (and autosaves to the ScribeSession) until then,
// so there are no mid-session AI passes overwriting one another. Filling is repeatable: edit
// the transcript, fill again, as often as needed. Gated behind :ai_scribe.
//
// Flow: open -> click the mic to start dictating -> talk (transcript autosaves) ->
//       Fill document -> one full-transcript Gai pass fills the fields -> unmask ->
//       review & edit (or reopen, fix the transcript, and fill again).
export default class extends ApplicationController {
  static targets = ['launcher', 'panel', 'mask', 'transcript', 'transcriptArea', 'status', 'doneButton', 'resumePrompt', 'resumeMeta', 'sessionField', 'maskCaption', 'maskCaptionText', 'maskCancel', 'maskSubtext', 'clearMarksButton', 'panelHint', 'footer', 'unavailableNotice'];

  // receivableIds: the control ids the AI Scribe may write, from CustomForms::ScribeFieldSpec
  // (server-decided via the Receive-from-AI flag). Empty = fill nothing.
  // sessionPath + context ids: the ScribeSession endpoints (transcript autosave, pause/resume).
  static values = {
    receivableIds: Array,
    // Designer defaults per control id (ScribeFieldSpec#defaults_by_id). A field showing
    // exactly its default counts as unanswered — fillable — while any other value is a
    // human answer the scribe never overwrites.
    fieldDefaults: Object,
    sessionPath: String,
    patientId: String,
    templateId: String,
    disciplineId: String,
    documentableType: String,
    documentableId: String,
  };

  static ACTIVE_CLASS = 'ai-scribe-active';
  static DICTATING_CLASS = 'ai-scribe-dictation-started';
  static NO_DICTATION_CLASS = 'ai-scribe-dictation-unavailable';
  static FILLED_CLASS = 'ai-scribe-field--filled';
  // Below this confidence a filled field is NAMED in the strip at the top — deliberately no
  // color of its own: a second color taught people to check only the flagged fields and
  // skim the rest, and everything AI-written deserves the same review.
  static LOW_CONFIDENCE_BELOW = 0.6;
  static NEEDS_VALUE_CLASS = NEEDS_VALUE_CLASS;

  static SAVE_DEBOUNCE = 800; // ms of quiet before the transcript autosaves to the session
  static POLL_MS = 2000; // poll cadence while a fill/finalize runs server-side
  static POLL_MAX_TICKS = 150; // give up after ~5 minutes (big forms can take Gemma ~3, plus queue wait)

  // Shown via _showSessionNotice when the server-side session can't be had — without one
  // nothing autosaves and a fill has nowhere to run, so the scribe must say so out loud.
  static MSG_OWNED_BY_OTHER =
    "Another therapist's dictation is attached to this document, so the AI Scribe isn't available here.";

  static MSG_SESSION_FAILED =
    "Something went wrong setting up the AI Scribe, so your dictation isn't being saved. Close this panel and try again.";

  connect() {
    this.aiFilled = new Set(); // provenance: fields the scribe wrote
    this.mergeableIds = null; // human-authored ids sent with the last fill — merge-sanctioned
    this.status = 'idle';
    this.sessionId = null;
    this.sessionRequestInFlight = false;
    this.pollTimer = null;
    this._boundSyncLayout = this._syncLayout.bind(this);
    // Reviewing an AI-filled field (any human edit) clears its provenance marking and hands
    // ownership to the human, so the marks converge on "still unreviewed".
    this._boundHumanEdit = this._onHumanEdit.bind(this);
    this.element.addEventListener('input', this._boundHumanEdit);
    this.element.addEventListener('change', this._boundHumanEdit);
    // Escape closes the open scribe panel (see _onKeydown; the instructions panel gets the
    // same treatment from the form template's own script).
    this._boundKeydown = this._onKeydown.bind(this);
    document.addEventListener('keydown', this._boundKeydown);
    this._setStatus('idle');
  }

  disconnect() {
    clearTimeout(this.saveTimer);
    clearInterval(this.pollTimer);
    window.removeEventListener('resize', this._boundSyncLayout);
    window.removeEventListener('scroll', this._boundSyncLayout);
    this.element.removeEventListener('input', this._boundHumanEdit);
    this.element.removeEventListener('change', this._boundHumanEdit);
    document.removeEventListener('keydown', this._boundKeydown);
  }

  // Escape closes the open scribe panel, same as its Cancel button — dictation stops, work
  // is kept (the transcript is autosaved), and reopening restores it. Ignored while a fill
  // pass is running (the mask pill's × is the deliberate way to stop waiting) and when a
  // Bootstrap modal is open (the modal owns Escape).
  _onKeydown(event) {
    if (event.key !== 'Escape') return;
    if (!this.element.classList.contains(this.constructor.ACTIVE_CLASS)) return;
    if (document.body.classList.contains('modal-open')) return;
    if (this.status === 'finalizing') return;
    this.cancel();
  }

  // ----- lifecycle -----

  open(event) {
    if (event) event.preventDefault();
    this.element.classList.add(this.constructor.ACTIVE_CLASS);
    // Take over the sidebar: close the instructions panel (revealing its eye toggle), like
    // clicking its own close button. The reference panel is hidden via CSS while
    // .ai-scribe-active is set. Then reveal the scribe column + the field mask.
    this._closeInstructions();
    this._reveal(this.maskTarget);
    this._reveal(this.panelTarget);
    if (this.hasLauncherTarget) this.launcherTarget.hidden = true;
    // Don't auto-listen — show the "click the mic to start" affordance and wait for the
    // therapist to start dictation deliberately (see startDictation).
    this._showMicPrompt();
    this._setStatus('ready');
    // Keep the floating caption centered over the form card and the panel's bottom pinned to
    // the bottom of the browser window, on open and on every scroll/resize while open.
    this._syncLayout();
    window.addEventListener('resize', this._boundSyncLayout);
    window.addEventListener('scroll', this._boundSyncLayout, { passive: true });
    // Find-or-create the server-side ScribeSession (may instead surface a resume prompt).
    this._ensureSession();
  }

  // The therapist clicked the big mic. The Soniox voice-dictation controller (mounted on the
  // transcript wrap) starts the actual capture; here we just move the UI into its listening
  // state and drop the start affordance. Idempotent, so later stop/restart clicks on the mic
  // don't re-trigger it.
  startDictation() {
    if (!this.hasTranscriptAreaTarget) return;
    // No mic possible here — never claim we're listening.
    if (!this._dictationAvailable()) return;
    if (this.transcriptAreaTarget.classList.contains(this.constructor.DICTATING_CLASS)) return;
    this.transcriptAreaTarget.classList.add(this.constructor.DICTATING_CLASS);
    this._setStatus('listening');
  }

  // The voice-dictation controller ended its session: the therapist clicked the mic, pressed
  // Escape, typed in the transcript, the stream failed, or one of its own limits fired (no speech
  // for a while, or the session ceiling). The status label follows the mic. A limit stop says why
  // and how to continue, so a quiet stretch in a session never leaves "Listening..." on a panel
  // that has stopped transcribing. Only a listening panel changes: a fill in progress or finished
  // keeps its own status.
  onDictationStopped(event) {
    if (this.status !== 'listening') return;
    const reason = event?.detail?.reason;
    if (reason === 'idle_stopped') {
      this._setStatus('paused_idle');
    } else if (reason === 'session_limit_stopped') {
      this._setStatus('paused_limit');
    } else {
      this._setStatus('ready');
    }
  }

  // Fill document — run a full-transcript pass server-side, then unmask for editing. Not a
  // one-way door: the therapist can reopen, edit the transcript, and fill again. With no
  // session or nothing said, there's nothing to fill: just hand the form back.
  finish(event) {
    if (event) event.preventDefault();
    if (this._transcript().length === 0) {
      this._teardown();
      this._setStatus('done');
      return;
    }
    if (!this.sessionId) {
      // Words with no server session (the create failed at open): nothing has been
      // autosaving and a fill has nowhere to run — never claim success. Surface it, keep
      // the transcript on screen, and retry the session so the next attempt can work.
      this._showSessionNotice(this.constructor.MSG_SESSION_FAILED);
      this._setStatus('error');
      this._ensureSession();
      return;
    }
    this._setStatus('finalizing');
    this._lockPanel(true);
    this._maskBusy(true);
    this._postFill('finalize')
      .then(() => this._pollForResult())
      .catch(() => {
        this._lockPanel(false);
        this._maskBusy(false);
        this._setStatus('error');
      });
  }

  // The × on the mask pill while the note is being filled: stop waiting and open the note.
  // The server-side pass finishes on its own; its result just isn't applied here.
  cancelFinalize(event) {
    if (event) event.preventDefault();
    clearInterval(this.pollTimer);
    this._lockPanel(false);
    this._teardown();
    this._setStatus('idle');
  }

  // Cancel — leave without finalizing. Stop dictation, unmask, restore the launcher.
  cancel(event) {
    if (event) event.preventDefault();
    this._teardown();
    this._setStatus('idle');
  }

  // Hide the scribe WITHOUT discarding its work — the transcript and any filled fields stay
  // in place, and reopening the launcher restores everything. Used when the therapist opens
  // the instructions panel, since the two can't be open at once.
  hide() {
    if (!this.element.classList.contains(this.constructor.ACTIVE_CLASS)) return;
    this._teardown();
  }

  // Clear / start over — stop dictation, wipe the transcript and the fields the scribe wrote,
  // and return to the "click the mic to start" state.
  clear(event) {
    if (event) event.preventDefault();
    this._stopDictation();
    if (this.hasTranscriptTarget) this.transcriptTarget.value = '';
    const groups = this._datumGroups();
    this.aiFilled.forEach((entry) => {
      if (typeof entry === 'string') {
        // A choice group the scribe touched: return it to unset (toggles back to their
        // neutral empty-value radio, everything else unchecked, detail texts cleared).
        // Each clear dispatches the same event the appliers did — without it, auto-save's
        // form history keeps its pre-clear snapshot and can resurrect the AI text the
        // therapist explicitly discarded.
        (groups.get(entry) || []).forEach((el) => {
          if (el.type === 'checkbox') el.checked = false;
          else if (el.type === 'radio') el.checked = el.value === '';
          else if (el.dataset.behavior === 'hidden-field') {
            // Impulse autocomplete part: clear the submitted value and its visible display.
            el.value = '';
            el.dataset.text = '';
            const visible = el.closest('.awc-autocomplete-control')?.querySelector('.awc-autocomplete-input');
            if (visible) visible.value = '';
          } else if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') el.value = '';
          const kind =
            el.type === 'checkbox' || el.type === 'radio' || el.dataset.behavior === 'hidden-field'
              ? 'change'
              : 'input';
          el.dispatchEvent(new Event(kind, { bubbles: true }));
          el.closest('.form-group')?.classList?.remove(this.constructor.FILLED_CLASS);
          el.classList.remove(this.constructor.FILLED_CLASS);
        });
      } else {
        entry.value = '';
        const editor = this._editorFor(entry);
        if (editor) editor.setData('');
        // Same reason as above — and date inputs also get change, matching _applyToDateInput
        // (the visit-note session-time chain and date-conflict check listen for change).
        entry.dispatchEvent(new Event('input', { bubbles: true }));
        if (entry.type === 'date' || entry.type === 'datetime-local') {
          entry.dispatchEvent(new Event('change', { bubbles: true }));
        }
        const marks = [this.constructor.FILLED_CLASS];
        entry.classList.remove(...marks);
        if (entry.id) {
          this.element.querySelector(`.wysiwyg-ckeditor[data-id="${CSS.escape(entry.id)}"]`)?.classList?.remove(...marks);
        }
        entry.closest('.form-group')?.classList?.remove(...marks);
      }
    });
    this.aiFilled.clear();
    this.element.querySelector('.ai-scribe-questions')?.remove();
    this._showClearMarks();
    // Persist the emptied transcript so a later resume doesn't resurrect what was cleared.
    this._saveTranscript();
    this._showMicPrompt();
    this._setStatus('ready');
  }

  // ----- capture -----

  // Fires on every change to the transcript, including text inserted by the voice-dictation
  // controller (it dispatches an input event). Capture only — the transcript autosaves to the
  // ScribeSession, and NO AI pass runs until the therapist clicks Fill document (a single
  // fill on demand beats mid-session passes overwriting one another).
  onTranscriptInput() {
    clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this._saveTranscript(), this.constructor.SAVE_DEBOUNCE);
  }

  // ----- fill (real: Gai via the ScribeSession finalize endpoint, on demand) -----

  // POST finalize, carrying the freshest transcript so the job routes what was actually
  // said (the debounced autosave may not have flushed yet), plus a snapshot of the live
  // receivable text fields so Gai can merge new transcript information into human-authored
  // text instead of skipping it. The server's fill endpoint still exists for a possible
  // future live-fill mode, but the client only ever finalizes.
  _postFill(action) {
    const snapshot = this._fieldSnapshot();
    // Remember which fields we declared human-authored: a value coming back for one of them
    // is a sanctioned merge, allowed past the ownership guard (see _applyFields).
    this.mergeableIds = new Set(snapshot.humanAuthored);
    return fetch(`${this.sessionPathValue}/${this.sessionId}/${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': this._csrfToken(), Accept: 'application/json' },
      body: JSON.stringify({
        transcript: this.hasTranscriptTarget ? this.transcriptTarget.value : '',
        field_values: snapshot.values,
        human_authored: snapshot.humanAuthored,
      }),
    }).then((resp) => (resp.ok ? resp.json() : Promise.reject(resp.status)));
  }

  // Snapshot the live text of every receivable free-text field (text inputs, text areas,
  // CKEditor) as it stands on the page, and flag which are human-authored: non-empty, not
  // the designer's prefill, and not (or no longer) AI-owned. Gai merges new transcript
  // information INTO human-authored text — the therapist's wording wins — while its own
  // previous output it may simply rewrite. Choice controls and composites are not
  // snapshotted: a human pick is never merged, only kept.
  _fieldSnapshot() {
    const values = {};
    const humanAuthored = [];
    const receivable = new Set(this.receivableIdsValue);
    this._datumGroups().forEach((els, id) => {
      if (!receivable.has(id) || els.length !== 1) return;
      const el = els[0];
      if (el.tagName === 'SELECT' || el.type === 'radio' || el.type === 'checkbox') return;
      if (el.type === 'date' || el.type === 'datetime-local') return;
      if (el.dataset.behavior === 'hidden-field') return;
      const editor = this._editorFor(el);
      const value = editor ? this._editorPlainText(editor) : el.value;
      if (typeof value !== 'string' || value.trim() === '') return;
      values[id] = value;
      const dflt = (this.fieldDefaultsValue || {})[id];
      const isDefault = dflt != null
        && value.replace(/\s+/g, ' ').trim() === dflt.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      const aiOwned = this.aiFilled.has(el) || this.aiFilled.has(id);
      if (!aiOwned && !isDefault) humanAuthored.push(id);
    });
    return { values, humanAuthored };
  }

  // A CKEditor field's content as plain text with paragraph breaks — the same dialect the
  // scribe writes back, so Gai sees current values in the format it produces.
  _editorPlainText(editor) {
    const html = editor.getData().replace(/<\/p>/gi, '\n\n').replace(/<br\s*\/?>/gi, '\n');
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent.replace(/\u00a0/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
  }

  // Poll the session until the finalize pass lands (status reaches finalized), then apply the
  // result and hand the form back. A fill that falls back to the self-hosted model waits its
  // turn there behind other clinics, so the window is generous; on timeout the UI recovers to
  // a retryable state.
  _pollForResult() {
    clearInterval(this.pollTimer);
    let ticks = 0;
    let lastHeartbeat = null;
    this.pollTimer = setInterval(() => {
      ticks += 1;
      if (ticks > this.constructor.POLL_MAX_TICKS) {
        clearInterval(this.pollTimer);
        this._lockPanel(false);
        this._maskBusy(false);
        this._setStatus('error');
        return;
      }
      fetch(`${this.sessionPathValue}/${this.sessionId}`, {
        headers: { 'X-CSRF-Token': this._csrfToken(), Accept: 'application/json' },
      })
        .then((resp) => (resp.ok ? resp.json() : Promise.reject(resp.status)))
        .then((json) => {
          // Only the fill job moves the status off `finalizing`: to `finalized` on success,
          // or back to `in_progress` when the pass failed (see Scribe::FillJob's rescue).
          // Neither running nor finalized means the pass ended without a result — fail now
          // instead of leaving the therapist behind the mask until the poll timeout.
          if (json.status !== 'finalizing' && json.status !== 'finalized') {
            clearInterval(this.pollTimer);
            this._lockPanel(false);
            this._maskBusy(false);
            this._setStatus('error');
            return;
          }
          if (json.status !== 'finalized') {
            // Progress heartbeats from the gai server: a fresh beat proves the run is alive
            // (merely slow, or waiting in gai's GPU queue), so reset the timeout clock and
            // tell the therapist what's actually happening.
            if (json.last_fill_heartbeat_at && json.last_fill_heartbeat_at !== lastHeartbeat) {
              lastHeartbeat = json.last_fill_heartbeat_at;
              ticks = 0;
              if (this.hasMaskSubtextTarget) {
                this.maskSubtextTarget.textContent = json.fill_heartbeat_detail === 'queued'
                  ? 'Waiting for our turn with the AI — hang tight.'
                  : 'Still working on it — almost there.';
              }
            }
            return;
          }
          clearInterval(this.pollTimer);
          this._applyRoutingResult(json.routing_result);
          markRequiredEmpties(this.element);
          this._showClearMarks();
          this._teardown();
          this._setStatus('done');
          this._renderOpenQuestions(json.routing_result);
        })
        .catch(() => {}); // transient poll failure — try again next tick
    }, this.constructor.POLL_MS);
  }

  // Write Gai's routed values into the form. Only Receive-from-AI fields (receivableIdsValue);
  // never overwrites something the user typed or a previously saved value (provenance guards
  // live in each applier). Every standard control shape has its own dialect:
  //   - text / textarea / filtered / select-autocomplete: one input, set its value
  //   - wysiwyg: write through the CKEditor instance
  //   - date / datetime: normalized ISO into native date inputs
  //   - radio buttons: one shared-name radio group, check the matching value
  //   - checkboxes: per-option inputs keyed by parameterized title — additive (never unchecks)
  //   - checkboxes-with-text: like checkboxes, plus an optional per-option detail text
  //   - toggles: per-option three-state radio pairs, answered as { key: choice }
  _applyRoutingResult(result) {
    const fields = Array.isArray(result?.fields) ? result.fields : [];
    if (fields.length === 0) return;
    const groups = this._datumGroups();
    const receivable = new Set(this.receivableIdsValue);
    // The appliers dispatch input/change so auto-save and dependent controls react; the guard
    // keeps _onHumanEdit from mistaking those synchronous events for a human review.
    this.applyingFill = true;
    try {
      this._applyFields(fields, groups, receivable);
    } finally {
      this.applyingFill = false;
    }
  }

  _applyFields(fields, groups, receivable) {
    fields.forEach((f) => {
      if (!receivable.has(f.id) || f.value == null || f.value === '') return;
      const els = groups.get(f.id);
      if (!els || els.length === 0) return;

      const dflt = (this.fieldDefaultsValue || {})[f.id];
      const radios = els.filter((el) => el.type === 'radio');
      const checks = els.filter((el) => el.type === 'checkbox');
      if (radios.length && radios.every((el) => /\[datum\]\[[^\]]+\]$/.test(el.name))) {
        this._applyRadioGroup(f.id, radios, f.value, dflt);
      } else if (radios.length) {
        this._applyToggleGroup(f.id, radios, f.value);
      } else if (checks.length && checks.some((el) => el.name.includes('[title]['))) {
        this._applyChecksWithText(f.id, els, f.value);
      } else if (checks.length) {
        this._applyCheckboxGroup(f.id, checks, f.value);
      } else if (f.value && typeof f.value === 'object' && !Array.isArray(f.value)) {
        // Composite smart controls (prognosis, therapy recommendations): object keyed by
        // part — dropdown parts are Impulse autocompletes, text parts plain inputs.
        this._applyCompositeParts(f.id, els, f.value, dflt);
      } else if (typeof f.value === 'string') {
        const el = els.find((e) => e.type === 'date' || e.type === 'datetime-local') || els[0];
        if (el.type === 'date' || el.type === 'datetime-local') {
          this._applyToDateInput(el, f.value);
        } else {
          // A value for a field we declared human-authored in the fill request is a
          // sanctioned merge of the therapist's text and new transcript information —
          // apply it even though the ownership guard would otherwise refuse.
          const merged = !!(this.mergeableIds && this.mergeableIds.has(f.id));
          const editor = this._editorFor(el);
          if (editor) {
            this._applyToEditor(el, editor, f.value, dflt, merged);
          } else {
            this._applyToInput(el, f.value, dflt, merged);
          }
        }
      }
    });
  }

  // Gai reports a 0.0–1.0 confidence on every value. Fields the scribe actually wrote below
  // the threshold are collected by label and NAMED in the strip at the top — they look like
  // every other filled field on the form.
  _lowConfidenceLabels(result) {
    const fields = Array.isArray(result?.fields) ? result.fields : [];
    const groups = this._datumGroups();
    const labels = [];
    fields.forEach((f) => {
      if (!(typeof f.confidence === 'number' && f.confidence < this.constructor.LOW_CONFIDENCE_BELOW)) return;
      const els = groups.get(f.id);
      if (!els || els.length === 0) return;
      if (!this.aiFilled.has(f.id) && !els.some((el) => this.aiFilled.has(el))) return;
      const label = els[0].closest('.form-group')?.querySelector('label .cfe-label_text')?.textContent?.trim();
      labels.push(label || 'an unlabeled field');
    });
    return labels;
  }

  // The eraser next to the launcher: dismiss every scribe marking at once — teal filled,
  // amber low-confidence, orange needs-value — without touching any field's value. It is a
  // mass "reviewed": ownership passes to the therapist (aiFilled empties), so a later fill
  // treats these fields as human-authored (merge, never rewrite), exactly as if each had
  // been reviewed by hand.
  clearMarkings(event) {
    if (event) event.preventDefault();
    clearAllMarks(this.element);
    this.aiFilled.clear();
    if (this.hasClearMarksButtonTarget) this.clearMarksButtonTarget.hidden = true;
  }

  // Reveal the eraser only once a fill pass has actually left markings to clear.
  _showClearMarks() {
    if (!this.hasClearMarksButtonTarget) return;
    this.clearMarksButtonTarget.hidden = !anyMarks(this.element);
  }

  // Any human edit to an AI-filled field clears its provenance marks and hands ownership to
  // the human — the marking means "still unreviewed", and a reviewed field must never keep
  // reading as raw AI output (nor be overwritten by a later pass).
  _onHumanEdit(event) {
    if (this.applyingFill) return;
    const t = event.target;
    if (!t || !(t instanceof Element)) return;
    let el = null;
    const host = t.closest?.('.wysiwyg-ckeditor');
    if (host?.dataset?.id) el = document.getElementById(host.dataset.id);
    if (!el && t.name && /\[datum\]\[/.test(t.name)) el = t;
    if (!el) {
      // Impulse autocomplete: edits land on the display input; the value carrier is the
      // sibling hidden field.
      const control = t.closest?.('.awc-autocomplete-control');
      el = control?.querySelector('[data-behavior="hidden-field"]') || null;
    }
    if (!el || !el.name) return;
    const id = el.name.match(/\[datum\]\[([^\]]+)\]/)?.[1];
    // Required-and-still-empty cue: re-check just this field on any edit, whatever its
    // provenance — typing into a flagged field clears it, emptying it brings it back.
    if (id) refreshNeedsValue(this.element, id);
    const hadEl = this.aiFilled.delete(el);
    const hadId = id ? this.aiFilled.delete(id) : false;
    if (hadEl || hadId) {
      const marks = [this.constructor.FILLED_CLASS];
      [el, host, el.closest('.form-group')].forEach((n) => {
        n?.classList?.remove(...marks);
        clearMarkTitle(n);
      });
    }
    // This edit may have cleared the last highlight (a review, or a required field gaining
    // a value) — retire the Clear highlights button the moment nothing is left to clear.
    this._showClearMarks();
  }

  // All rendered datum form elements, grouped by control id. Hidden inputs (check_box_tag's
  // "0" companions) are excluded from choice classification — EXCEPT Impulse autocomplete
  // value carriers (data-behavior="hidden-field"), which are the real submitted inputs for
  // the composite smart controls' dropdowns.
  _datumGroups() {
    const els = this.element.querySelectorAll(
      'input[name*="[datum]["], select[name*="[datum]["], textarea[name*="[datum]["]'
    );
    const groups = new Map();
    els.forEach((el) => {
      if (el.disabled) return;
      if (el.type === 'hidden' && el.dataset.behavior !== 'hidden-field') return;
      const m = el.name.match(/\[datum\]\[([^\]]+)\]/);
      if (!m) return;
      if (!groups.has(m[1])) groups.set(m[1], []);
      groups.get(m[1]).push(el);
    });
    return groups;
  }

  // Single-choice radios (shared name, per-option value). Only fills a group nobody has
  // answered: nothing checked, OR only the designer's default checked — a template-shipped
  // default is pre-checked from the moment the document renders, so it must NOT count as a
  // human answer (it was silently blocking the AI on any radio group with a default). A
  // checked non-default option is a human choice and is never overwritten.
  _applyRadioGroup(id, radios, value, dflt) {
    if (typeof value !== 'string') return;
    const onlyDefaultChecked = radios.every((r) => !r.checked || (dflt != null && r.value === dflt));
    const owned = this.aiFilled.has(id) || onlyDefaultChecked;
    if (!owned) return;
    const target = radios.find((r) => r.value === value) || this._matchByLabel(radios, value);
    if (!target || target.checked) return;
    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
    this.aiFilled.add(id);
    this._flashGroup(target);
  }

  // Multi-choice checkboxes: ADDITIVE — checks the options the transcript supports, never
  // unchecks anything (designer defaults and human choices stay).
  _applyCheckboxGroup(id, checks, value) {
    const values = Array.isArray(value) ? value : [value];
    let changed = false;
    values.forEach((v) => {
      if (typeof v !== 'string' || v === '') return;
      const target = this._matchByLabel(checks, v);
      if (target && !target.checked) {
        target.checked = true;
        target.dispatchEvent(new Event('change', { bubbles: true }));
        changed = true;
      }
    });
    if (changed) {
      this.aiFilled.add(id);
      this._flashGroup(checks[0]);
    }
  }

  // Checkboxes-with-text: value may be an object { optionValue: "detail text" }, an array of
  // option values, or a single string. Checks matching options (additive) and fills each
  // option's detail input when it's empty.
  _applyChecksWithText(id, els, value) {
    let entries;
    if (Array.isArray(value)) entries = value.map((v) => [v, '']);
    else if (typeof value === 'object') entries = Object.entries(value);
    else entries = [[value, '']];

    const checks = els.filter((el) => el.type === 'checkbox');
    let changed = false;
    entries.forEach(([v, text]) => {
      if (typeof v !== 'string' || v === '') return;
      const box = this._matchByLabel(checks, v);
      if (!box) return;
      if (!box.checked) {
        box.checked = true;
        box.dispatchEvent(new Event('change', { bubbles: true }));
        changed = true;
      }
      if (typeof text === 'string' && text !== '') {
        const key = box.name.match(/\[title\]\[([^\]]+)\]$/)?.[1];
        const textInput = key
          ? els.find((el) => el.name.endsWith(`[text-input][${key}]`))
          : null;
        if (textInput && (textInput.value.trim() === '' || this.aiFilled.has(id))) {
          textInput.value = text;
          textInput.dispatchEvent(new Event('input', { bubbles: true }));
          changed = true;
        }
      }
    });
    if (changed) {
      this.aiFilled.add(id);
      this._flashGroup(checks[0] || els[0]);
    }
  }

  // Toggles: value is { key: chosenChoice } per the field spec's { key, label, choices }
  // options. Each option is only filled while it sits in its neutral "off" state (the
  // empty-value radio) — a chosen left/right answer is left alone.
  _applyToggleGroup(id, radios, value) {
    if (typeof value !== 'object' || Array.isArray(value) || value === null) return;
    let changed = false;
    Object.entries(value).forEach(([key, choice]) => {
      if (typeof choice !== 'string' || choice === '') return;
      const optionRadios = radios.filter((r) => r.name.endsWith(`[${key}]`));
      if (optionRadios.length === 0) return;
      const offSelected = optionRadios.some((r) => r.value === '' && r.checked);
      if (!offSelected && !this.aiFilled.has(id)) return;
      const target = optionRadios.find((r) => r.value === choice);
      if (!target || target.checked) return;
      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
      changed = true;
    });
    if (changed) {
      this.aiFilled.add(id);
      this._flashGroup(radios[0]);
    }
  }

  // Composite smart controls (prognosis, therapy recommendations): value is an object mapping
  // each part's key to a choice value or text. Dropdown parts submit through an Impulse
  // autocomplete hidden field; text parts are plain inputs/textareas. Per-part ownership uses
  // the part's designer default (these dropdowns are ALWAYS pre-selected with one): showing
  // the default counts as unanswered, anything else is a human choice and stays.
  _applyCompositeParts(id, els, value, defaults) {
    const defs = defaults && typeof defaults === 'object' ? defaults : {};
    let changed = false;
    Object.entries(value).forEach(([key, val]) => {
      if (typeof val !== 'string' || val === '') return;
      const el = els.find((e) => e.name.endsWith(`[${key}]`));
      if (!el) return;
      const partDefault = typeof defs[key] === 'string' ? defs[key] : null;
      if (el.dataset.behavior === 'hidden-field') {
        if (this._applyToAutocomplete(id, el, val, partDefault)) changed = true;
      } else if (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') {
        const before = el.value;
        this._applyToInput(el, val, partDefault);
        if (el.value !== before) changed = true;
      }
    });
    if (changed) {
      this.aiFilled.add(id);
      this._flashGroup(els[0]);
    }
  }

  // Write one Impulse autocomplete part: only when it still shows its designer default (or
  // nothing), only to a value that exists among its options, updating the hidden submitted
  // field AND the visible display input so what the therapist sees matches what will save.
  _applyToAutocomplete(id, hiddenEl, val, partDefault) {
    const owned = this.aiFilled.has(id) || hiddenEl.value === '' || (partDefault !== null && hiddenEl.value === partDefault);
    if (!owned || hiddenEl.value === val) return false;
    const control = hiddenEl.closest('.awc-autocomplete-control');
    const root = control ? control.parentElement : null;
    const option = (root || this.element).querySelector(`[role="option"][value="${CSS.escape(val)}"]`);
    if (!option) return false; // not among this dropdown's choices — never force a value
    const title = (option.getAttribute('data-text') || option.textContent || '').trim();
    hiddenEl.value = val;
    hiddenEl.dataset.text = title;
    const visible = (root || control)?.querySelector('.awc-autocomplete-input');
    if (visible) visible.value = title;
    hiddenEl.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  // Open questions from the final pass — concrete "confirm X" items the model left rather
  // than guessing. Strictly more actionable than a highlight, so they get a dismissible strip
  // at the top of the form once the mask lifts.
  _renderOpenQuestions(result) {
    const questions = (Array.isArray(result?.open_questions) ? result.open_questions : [])
      .filter((q) => typeof q === 'string' && q.trim() !== '')
      // The model sometimes cites internal control ids ("(control_891959_72328)") in its
      // questions — meaningless noise to a therapist, so scrub them regardless of what the
      // instruction set promises.
      .map((q) => q
        .replace(/\s*\(\s*control_[0-9_]+\s*\)/gi, '')
        .replace(/\bcontrol_[0-9_]+\b/gi, 'that field')
        .replace(/\s{2,}/g, ' ')
        .trim())
      .filter((q) => q !== '');
    const shaky = this._lowConfidenceLabels(result);
    if (shaky.length > 0) {
      questions.unshift(`I wasn't fully confident about ${shaky.join(', ')} — give ${shaky.length === 1 ? 'it' : 'those'} a closer look.`);
    }
    if (questions.length === 0) return;
    this.element.querySelector('.ai-scribe-questions')?.remove();
    const box = document.createElement('div');
    box.className = 'alert alert-info alert-dismissible ai-scribe-questions';
    const heading = document.createElement('strong');
    heading.innerHTML = '<i class="bi bi-stars mr-1"></i>';
    heading.append(`The AI left ${questions.length === 1 ? 'one thing' : questions.length + ' things'} for you to check:`);
    const list = document.createElement('ul');
    questions.forEach((q) => {
      const li = document.createElement('li');
      li.textContent = q;
      list.appendChild(li);
    });
    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'close';
    dismiss.setAttribute('data-dismiss', 'alert');
    dismiss.setAttribute('aria-label', 'Dismiss');
    dismiss.innerHTML = '<span aria-hidden="true">&times;</span>';
    box.append(dismiss, heading, list);
    const body = this.element.querySelector('.card-body');
    (body || this.element).prepend(box);
  }

  // Match a choice input by its label text (the canonical option title the field spec sends),
  // falling back to the input's own value attribute.
  _matchByLabel(inputs, value) {
    const needle = value.trim().toLowerCase();
    return (
      inputs.find((el) => (this._labelTextFor(el) || '').trim().toLowerCase() === needle) ||
      inputs.find((el) => (el.value || '').trim().toLowerCase() === needle)
    );
  }

  _labelTextFor(el) {
    if (!el.id) return null;
    const label = this.element.querySelector(`label[for="${CSS.escape(el.id)}"] span`);
    return label ? label.textContent : null;
  }

  _flashGroup(el) {
    const group = el.closest('.form-group') || el;
    group.classList.add(this.constructor.FILLED_CLASS);
    setMarkTitle(group, MARK_TITLES.filled);
    this._flash(group);
  }

  // Smart date/time fields (scribable smart controls). The model returns "YYYY-MM-DD" or
  // "YYYY-MM-DD HH:MM"; native inputs want exactly "YYYY-MM-DD" (date) or "YYYY-MM-DDTHH:MM"
  // (datetime-local). A date-only value for a date+time field is left for the therapist
  // rather than inventing a midnight time. Fires change as well as input so the visit-note
  // session-time chain and date-conflict check react like a hand-entered date.
  _applyToDateInput(el, value) {
    const m = value.trim().match(/^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}:\d{2}))?/);
    if (!m) return;
    let v;
    if (el.type === 'datetime-local') {
      if (!m[2]) return;
      v = `${m[1]}T${m[2]}`;
    } else {
      v = m[1];
    }
    const aiOwned = this.aiFilled.has(el) || el.value === '';
    if (!aiOwned || el.value === v) return;
    el.value = v;
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
    this._flagFilled(el, el);
  }

  // The CKEditor instance for a wysiwyg field's hidden source textarea, if this is one. The
  // editor mounts on the sibling .wysiwyg-ckeditor div keyed by data-id (see the wysiwyg
  // control partial + voice_dictation_controller's prepareInsertPoint).
  _editorFor(el) {
    if (!el.id) return null;
    const host = this.element.querySelector(`.wysiwyg-ckeditor[data-id="${CSS.escape(el.id)}"]`);
    return host?.ckeditorInstance || null;
  }

  _applyToInput(el, value, dflt, merged = false) {
    // Empty OR showing exactly the designer's prefill (default_text / select default) counts
    // as unanswered; anything else is a human answer — updatable only as a sanctioned merge.
    const current = el.value.trim();
    const aiOwned = this.aiFilled.has(el) || current === '' || (dflt != null && current === dflt.trim());
    if ((!aiOwned && !merged) || el.value === value) return;
    el.value = value;
    // Let the form's auto-save (and anything else listening) snapshot the new value.
    el.dispatchEvent(new Event('input', { bubbles: true }));
    this._flagFilled(el, el);
  }

  _applyToEditor(el, editor, value, dflt, merged = false) {
    const currentText = editor.getData().replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
    const defaultText = dflt != null ? dflt.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : null;
    const aiOwned = this.aiFilled.has(el) || currentText === '' || (defaultText !== null && currentText === defaultText);
    if (!aiOwned && !merged) return;
    const html = value
      .split(/\n+/)
      .map((p) => `<p>${this._escapeHtml(p)}</p>`)
      .join('');
    if (editor.getData() === html) return;
    editor.setData(html);
    // Keep the source textarea in sync so auto-save snapshots the filled value too.
    el.value = html;
    el.dispatchEvent(new Event('input', { bubbles: true }));
    const host = this.element.querySelector(`.wysiwyg-ckeditor[data-id="${CSS.escape(el.id)}"]`);
    this._flagFilled(el, host || el);
  }

  _flagFilled(el, highlightEl) {
    this.aiFilled.add(el);
    highlightEl.classList.add(this.constructor.FILLED_CLASS);
    setMarkTitle(highlightEl, MARK_TITLES.filled);
    this._flash(highlightEl);
  }

  // The landing flash is a one-shot Web Animation rather than a CSS class: no class is ever
  // applied, so there is no way for a flash to persist as a stuck background — the animation
  // object plays once, resolves to transparent, and is forgotten.
  _flash(node) {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    node.animate(
      [{ backgroundColor: 'rgba(255, 235, 59, 0.5)' }, { backgroundColor: 'transparent' }],
      { duration: 1200, easing: 'ease-out' }
    );
  }

  _escapeHtml(text) {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // ----- helpers -----

  _showMicPrompt() {
    if (!this.hasTranscriptAreaTarget) return;
    this.transcriptAreaTarget.classList.remove(this.constructor.DICTATING_CLASS);
    // Dictation structurally unavailable (insecure context, unsupported browser, no mic API):
    // skip the big-mic affordance entirely and go straight to type-or-paste.
    if (!this._dictationAvailable()) {
      this.transcriptAreaTarget.classList.add(this.constructor.NO_DICTATION_CLASS);
      if (this.hasTranscriptTarget) {
        this.transcriptTarget.placeholder = 'Type or paste your session notes here…';
      }
    }
  }

  // Mirrors the voice-dictation controller's own availability detection (it computes
  // unavailableReason on connect); falls back to the core secure-context check.
  _dictationAvailable() {
    const vd = this.hasTranscriptAreaTarget
      ? this.application.getControllerForElementAndIdentifier(this.transcriptAreaTarget, 'voice-dictation')
      : null;
    if (vd) return !vd.unavailableReason;
    return !!navigator.mediaDevices?.getUserMedia;
  }

  _syncLayout() {
    this._centerCaption();
    this._sizePanel();
  }

  // Pin the panel card's bottom edge to the bottom of the browser window, so Clear and Fill
  // are always on screen. Two guards make this stable:
  //  - Measure the COLUMN's top, not the card's. The card is the thing we're resizing, so
  //    measuring it feeds back: taller card -> taller page -> more scroll -> top drops ->
  //    taller card, forever (the infinite-scroll bug).
  //  - Never size for a top above the sticky offset (70px, .sticky-right-column). That caps
  //    the height at viewport-minus-offset once the card is stuck, ending the growth.
  _sizePanel() {
    if (!this.hasPanelTarget) return;
    const card = this.panelTarget.querySelector('.ai-scribe-panel');
    if (!card) return;
    const STICKY_TOP = 70;
    const top = Math.max(this.panelTarget.getBoundingClientRect().top, STICKY_TOP);
    card.style.height = `${Math.max(240, window.innerHeight - top - 10)}px`;
  }

  // Swap the floating mask pill between its two states: explaining the lock while scribing,
  // and "Filling in your document…" (pulsing, with a clickable ×) while the finalize pass runs.
  _maskBusy(busy) {
    if (this.hasMaskCaptionTarget) {
      this.maskCaptionTarget.classList.toggle('ai-scribe-mask__caption--busy', busy);
      // Hover reinforcement for the visible wait hint below.
      if (busy) {
        this.maskCaptionTarget.title = 'This can take a minute or two on bigger documents.';
      } else {
        this.maskCaptionTarget.removeAttribute('title');
      }
    }
    if (this.hasMaskCaptionTextTarget) {
      this.maskCaptionTextTarget.textContent = busy
        ? 'Filling in your document…'
        : 'Fill your document with the AI Scribe to review and edit it';
    }
    if (this.hasMaskCancelTarget) this.maskCancelTarget.hidden = !busy;
    if (this.hasMaskSubtextTarget) {
      this.maskSubtextTarget.hidden = !busy;
      // Reset to the default wait copy — heartbeat updates may have rewritten it last run.
      if (busy) {
        this.maskSubtextTarget.textContent =
          'Hang tight — filling everything in usually takes under a minute, a little longer for big documents.';
      }
    }
  }

  // Position the floating caption over the horizontal center of the form card (the masked field
  // region) rather than the viewport center — the scribe panel occupies the right third.
  _centerCaption() {
    if (!this.hasMaskTarget) return;
    const float = this.maskTarget.querySelector('.ai-scribe-mask__float');
    if (!float) return;
    const rect = this.maskTarget.getBoundingClientRect();
    if (rect.width === 0) return; // mask not visible yet
    float.style.left = `${Math.round(rect.left + rect.width / 2)}px`;
  }

  // Stop any running Soniox session directly (no re-trigger of startDictation, unlike clicking
  // the mic button). haltSession stops/abandons regardless of phase, keeping finalized text.
  _stopDictation() {
    if (!this.hasTranscriptAreaTarget) return;
    const vd = this.application.getControllerForElementAndIdentifier(this.transcriptAreaTarget, 'voice-dictation');
    if (vd && typeof vd.haltSession === 'function') vd.haltSession();
  }

  _teardown() {
    clearInterval(this.pollTimer);
    window.removeEventListener('resize', this._boundSyncLayout);
    window.removeEventListener('scroll', this._boundSyncLayout);
    this._maskBusy(false);
    this._stopDictation();
    this.element.classList.remove(this.constructor.ACTIVE_CLASS);
    this._hideEl(this.maskTarget);
    this._hideEl(this.panelTarget);
    this._lockPanel(false);
    if (this.hasLauncherTarget) this.launcherTarget.hidden = false;
  }

  _closeInstructions() {
    const instructions = this.element.querySelector('.form-instructions');
    const eye = this.element.querySelector('.instructions-open');
    if (instructions) instructions.classList.add('hidden');
    if (eye) eye.classList.remove('hidden');
  }

  _transcript() {
    return this.hasTranscriptTarget ? this.transcriptTarget.value.trim() : '';
  }

  _lockPanel(locked) {
    if (this.hasTranscriptTarget) this.transcriptTarget.disabled = locked;
    if (this.hasDoneButtonTarget) this.doneButtonTarget.disabled = locked;
  }

  _setStatus(state) {
    this.status = state;
    if (!this.hasStatusTarget) return;
    const labels = {
      idle: '',
      ready: '',
      listening: 'Listening…',
      paused_idle: 'Paused after a quiet stretch. Click the mic to keep going.',
      paused_limit: 'Paused after a long session. Click the mic to keep going.',
      finalizing: 'Filling in your document…',
      done: 'Filled in — review your document, or fill again any time',
      error: "Couldn't fill your document — try again",
    };
    this.statusTarget.textContent = labels[state] || '';
    this.statusTarget.dataset.state = state;
  }

  // ----- session persistence (ScribeSession endpoints) -----

  // Find-or-create the server-side session for this context. On a new document the server may
  // instead return a `resumable` candidate — we PROMPT, never auto-load. A failure surfaces
  // as the unavailable notice (see _showSessionNotice): without a session nothing autosaves
  // and a fill has nowhere to run, so pretending otherwise would quietly lose the dictation.
  _ensureSession() {
    if (this.sessionId || this.sessionRequestInFlight || !this.sessionPathValue) return;
    this._requestSession(null);
  }

  resumeSession(event) {
    if (event) event.preventDefault();
    this._requestSession('resume');
  }

  freshSession(event) {
    if (event) event.preventDefault();
    this._requestSession('fresh');
  }

  _requestSession(choice) {
    this.sessionRequestInFlight = true;
    const body = {
      patient_id: this.patientIdValue,
      custom_form_template_id: this.templateIdValue,
      discipline_id: this.disciplineIdValue,
      documentable_type: this.documentableTypeValue,
      documentable_id: this.documentableIdValue,
    };
    if (choice) body.choice = choice;
    fetch(this.sessionPathValue, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': this._csrfToken(), Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then((resp) => (resp.ok ? resp.json() : Promise.reject(resp.status)))
      .then((json) => {
        if (json.resumable) {
          this._showResumePrompt(json.resumable);
        } else {
          this._adoptSession(json);
        }
      })
      .catch((status) => {
        // 409: the document's dictation belongs to another therapist (sessions are personal;
        // see ScribeSessionsController#render_owned_by_other). Anything else is a transient
        // failure — either way the therapist must hear it, not dictate into a void.
        this._showSessionNotice(
          status === 409 ? this.constructor.MSG_OWNED_BY_OTHER : this.constructor.MSG_SESSION_FAILED
        );
      })
      .finally(() => {
        this.sessionRequestInFlight = false;
      });
  }

  _adoptSession(json) {
    this.sessionId = json.id;
    // Ride the session id along with the document save so the server can attach the session
    // to the newly saved document (CustomFormExtended#adopt_scribe_session).
    if (this.hasSessionFieldTarget) this.sessionFieldTarget.value = json.id;
    this._hideSessionNotice();
    this._hideResumePrompt();
    // Restore a previous transcript (resume, or reopening a saved document's session). Show it
    // with the corner mic rather than the big-mic overlay — there's already text; the mic is
    // still the way to continue dictating.
    if (json.transcript && this.hasTranscriptTarget && this.transcriptTarget.value.trim() === '') {
      this.transcriptTarget.value = json.transcript;
      if (this.hasTranscriptAreaTarget) this.transcriptAreaTarget.classList.add(this.constructor.DICTATING_CLASS);
    }
  }

  _showResumePrompt(resumable) {
    if (!this.hasResumePromptTarget) return;
    this._hideSessionNotice();
    if (this.hasResumeMetaTarget) {
      const when = resumable.updated_at ? new Date(resumable.updated_at).toLocaleString() : '';
      const preview = resumable.transcript_preview ? ` — “${resumable.transcript_preview}”` : '';
      this.resumeMetaTarget.textContent = `${when}${preview}`;
    }
    this.resumePromptTarget.hidden = false;
    if (this.hasTranscriptAreaTarget) this.transcriptAreaTarget.hidden = true;
    // While the resume choice is pending there is nothing to clear or fill yet — the hint
    // and the Clear / Fill document buttons would act on a transcript not chosen yet.
    if (this.hasPanelHintTarget) this.panelHintTarget.hidden = true;
    if (this.hasFooterTarget) this.footerTarget.hidden = true;
  }

  _hideResumePrompt() {
    if (this.hasResumePromptTarget) this.resumePromptTarget.hidden = true;
    if (this.hasTranscriptAreaTarget) this.transcriptAreaTarget.hidden = false;
    if (this.hasPanelHintTarget) this.panelHintTarget.hidden = false;
    if (this.hasFooterTarget) this.footerTarget.hidden = false;
  }

  // The scribe can't get a server session (most often: this document's dictation belongs to
  // another therapist; otherwise a transient failure). Without one nothing autosaves and a
  // fill has nowhere to run, so the fill affordances are withdrawn and the notice says why.
  // The transcript area only hides while it's empty — words the therapist could still copy
  // out are never hidden from them.
  _showSessionNotice(message) {
    this._stopDictation();
    if (this.hasUnavailableNoticeTarget) {
      this.unavailableNoticeTarget.textContent = message;
      this.unavailableNoticeTarget.hidden = false;
    }
    if (this.hasTranscriptAreaTarget && this._transcript().length === 0) this.transcriptAreaTarget.hidden = true;
    if (this.hasPanelHintTarget) this.panelHintTarget.hidden = true;
    if (this.hasFooterTarget) this.footerTarget.hidden = true;
  }

  // Counterpart to _showSessionNotice. Only the notice itself is dismissed here — the shared
  // panel elements are revealed by whichever state takes over (_adoptSession via
  // _hideResumePrompt, or _showResumePrompt), so the two prompts never fight over them.
  _hideSessionNotice() {
    if (this.hasUnavailableNoticeTarget) this.unavailableNoticeTarget.hidden = true;
  }

  _saveTranscript() {
    if (!this.sessionId || !this.sessionPathValue) return;
    fetch(`${this.sessionPathValue}/${this.sessionId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': this._csrfToken(), Accept: 'application/json' },
      body: JSON.stringify({ transcript: this.hasTranscriptTarget ? this.transcriptTarget.value : '' }),
    }).catch(() => {});
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || '';
  }

  _reveal(el) {
    if (el) el.hidden = false;
  }

  _hideEl(el) {
    if (el) el.hidden = true;
  }
}
